import { useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { useWorkflows } from 'src/graphql/entities/workflow/hooks/use-workflows';
import { GET_WORKFLOW_VERSIONS_QUERY } from 'src/graphql/entities/workflow/workflow.operations';
import {
  getWorkflowTriggerType,
  WorkflowTriggerType,
} from 'src/graphql/entities/workflow/workflow.trigger';
import { GetWorkflowVersionsQuery } from 'src/graphql/entities/workflow/workflow.types';
import { QueryVariables } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';

/** A workflow that can be assigned to, or forced onto, a trigger. */
export interface WorkflowByTriggerOption {
  workflowId: string;
  name: string;
  triggerType: WorkflowTriggerType;
  /** Always true unless the caller asked for inactive ones; nothing runs without one. */
  hasActiveVersion: boolean;
}

export interface UseWorkflowsByTriggerResult {
  workflowsByTrigger: Record<WorkflowTriggerType, WorkflowByTriggerOption[]>;
  loading: boolean;
}

export interface UseWorkflowsByTriggerOptions {
  /**
   * Also list workflows with no active version, flagged rather than omitted. Widens the query
   * to every version: the trigger type lives in a definition, so a draft-only workflow can
   * only be placed by reading its newest draft.
   */
  includeWithoutActiveVersion?: boolean;
}

/** Version edits happen on the calling page, so the cache is routinely stale by open time. */
const FETCH_POLICY = 'cache-and-network';

/** Every bucket present and empty, so a caller can index without a nullish check. */
export function emptyWorkflowsByTrigger(): Record<
  WorkflowTriggerType,
  WorkflowByTriggerOption[]
> {
  // Spelled out, so a new trigger type is a compile error rather than an empty select.
  return {
    [WorkflowTriggerType.InboundPhoneCall]: [],
    [WorkflowTriggerType.InboundSms]: [],
  };
}

/**
 * Lists one partner's workflows grouped by the trigger type their definition declares — pass
 * the platform partner for the platform's. Only workflows with an active version appear
 * unless `includeWithoutActiveVersion` is set, since the backend refuses the rest.
 *
 * The trigger type lives in the definition rather than a column, so one version is parsed per
 * workflow to place it.
 */
export function useWorkflowsByTrigger(
  partnerId?: number | null,
  { includeWithoutActiveVersion = false }: UseWorkflowsByTriggerOptions = {},
): UseWorkflowsByTriggerResult {
  const { showErrorAlert } = useNotifications();
  // Names come from the workflow rows. Without the platform scope the lookup misses and every
  // platform version is dropped for want of a name — an empty group rather than an error.
  const { workflows, loading: workflowsLoading } = useWorkflows(partnerId, {
    includePlatformLevelData: true,
    fetchPolicy: FETCH_POLICY,
  });

  const { data, loading: versionsLoading } = useQuery<
    GetWorkflowVersionsQuery,
    QueryVariables
  >(GET_WORKFLOW_VERSIONS_QUERY, {
    variables: isNullish(partnerId)
      ? undefined
      : {
          params: {
            filters: [
              { attribute: 'partnerId', operator: '=', value: String(partnerId) },
              ...(includeWithoutActiveVersion
                ? []
                : [{ attribute: 'isActive', operator: '=', value: 'true' }]),
            ],
            includeDeleted: false,
          },
        },
    skip: isNullish(partnerId),
    fetchPolicy: FETCH_POLICY,
    // Otherwise `ov-partner-ids` carries only the selected partner and a `partnerId = 0`
    // filter returns nothing. See the authLink in src/graphql/setup/config.ts.
    //
    // `ttl: 0`, as in useWorkflowVersions: the version mutations refetch this list, and a refetch
    // the 12h TTL link answers from its cache misses a draft activated since. The Assignments modal
    // would then see no active version and clear the partner's assignment.
    context: { includePlatformLevelData: true, ttl: 0 },
    onError: (fetchError) => {
      console.error(fetchError);
      showErrorAlert('Failed to load workflows. Please try again.');
    },
  });

  const workflowsByTrigger = useMemo(() => {
    const nameByWorkflowId = new Map(
      workflows.map((workflow) => [workflow.workflowId, workflow.name]),
    );

    // One version speaks for each workflow — the active one, else the newest. Picking per
    // workflow also collapses the several-active-versions case, which would list it twice.
    const speakingVersion = new Map<
      string,
      { definition?: string | null; isActive: boolean; version: number }
    >();

    (data?.getWorkflowVersion?.records ?? []).forEach((record) => {
      if (!record.workflowId) return;

      const candidate = {
        definition: record.definition,
        isActive: !!record.isActive,
        version: Number(record.version ?? 0),
      };
      const current = speakingVersion.get(String(record.workflowId));
      const wins =
        !current ||
        (candidate.isActive && !current.isActive) ||
        (candidate.isActive === current.isActive && candidate.version > current.version);

      if (wins) speakingVersion.set(String(record.workflowId), candidate);
    });

    const grouped = emptyWorkflowsByTrigger();

    speakingVersion.forEach((version, workflowId) => {
      const triggerType = getWorkflowTriggerType(version.definition);
      const name = nameByWorkflowId.get(workflowId);

      if (!triggerType || !name) return;

      grouped[triggerType].push({
        workflowId,
        name,
        triggerType,
        hasActiveVersion: version.isActive,
      });
    });

    Object.values(grouped).forEach((bucket) =>
      bucket.sort((a, b) => a.name.localeCompare(b.name)),
    );

    return grouped;
  }, [data?.getWorkflowVersion?.records, workflows]);

  return { workflowsByTrigger, loading: workflowsLoading || versionsLoading };
}
